import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  Divider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { fetchPost } from '../../api/common';
import { Profile } from '@par/shared/types/player';
import { useNavigate, useParams } from 'react-router-dom';

type PropTypes = {
  onUpdateProfile: () => void;
  profile: Profile;
};

export const ProfileAccount = ({ onUpdateProfile, profile }: PropTypes) => {
  const navigate = useNavigate();
  const { profileId } = useParams();

  useEffect(() => {
    if (!profileId || profileId !== profile.id) {
      navigate('/');
    }
  }, [profileId]);

  const handleCheck = async (
    type:
      | 'announcements'
      | 'tournaments'
      | 'dailiesReminder'
      | 'dailiesSummary'
      | 'ghostChallenges',
    checked: boolean
  ) => {
    const updatedProfile = {
      ...profile,
      emailNotificationPreferences: {
        ...profile.emailNotificationPreferences,
        [type]: checked,
      },
    };
    await fetchPost('/profile/email-notification-preferences', updatedProfile);
    onUpdateProfile();
  };

  const handleTournaments = async (checked: boolean) => {
    const updatedProfile = {
      ...profile,
      emailNotificationPreferences: {
        ...profile.emailNotificationPreferences,
        tournaments: checked,
      },
    };
    await fetchPost('/profile/email-notification-preferences', updatedProfile);
    onUpdateProfile();
  };

  return (
    <Container sx={{ display: 'flex', justifyContent: 'center' }}>
      <Stack
        spacing={2}
        sx={{ display: 'flex', justifyContent: 'center' }}
        width="600px"
      >
        <Typography variant="h5" mb={2} textAlign={'center'}>
          Email notification preferences
        </Typography>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Major announcements
              <Tooltip
                placement="top"
                title="Major PAR related news, feature releases, and announcements"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Switch
              checked={!!profile.emailNotificationPreferences.announcements}
              onChange={(e) => handleCheck('announcements', e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Tournament announcements
              <Tooltip
                placement="top"
                title="Upcoming tournament announcements and other tournament related info"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Switch
              checked={profile.emailNotificationPreferences.tournaments}
              onChange={(e) => handleCheck('tournaments', e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Dailies - reminder
              <Tooltip placement="top" title="A reminder to play your daily">
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            Coming Soon
            <Switch
              checked={profile.emailNotificationPreferences.dailiesReminder}
              onChange={(e) => handleCheck('dailiesReminder', e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Dailies - summary
              <Tooltip
                placement="top"
                title="A summary of the previous day's daily"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            Coming Soon
            <Switch
              checked={profile.emailNotificationPreferences.dailiesSummary}
              onChange={(e) => handleCheck('dailiesSummary', e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Ghost Challenges - reminder to purge
              <Tooltip
                placement="top"
                title="A reminder for you to purge ghost challenges send to you"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            Coming Soon
            <Switch
              checked={profile.emailNotificationPreferences.ghostChallenges}
              onChange={(e) => handleCheck('ghostChallenges', e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
      </Stack>
    </Container>
  );
};
